import React, { useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Modal, Button, Form } from "react-bootstrap";
import { Printer } from "react-feather";
import DutyListPrintPreview, { VIEW_TITLES } from "./DutyListPrintPreview";

const PAPER = {
  "A4L": { label: "A4 landscape", css: "A4 landscape", previewWidth: "297mm" },
  "A3L": { label: "A3 landscape", css: "A3 landscape", previewWidth: "420mm" },
  "A4P": { label: "A4 portrait", css: "A4 portrait", previewWidth: "210mm" },
};

const PREVIEW_ROWS = 60;

/**
 * Preview + print for the Duty List - same popup pattern as the petty cash
 * ledger and voucher prints: render the preview component to static markup,
 * write it into a blank window, print (the browser's own dialog also saves
 * as PDF). Table headers repeat on every page.
 *
 * Options beyond the WinForms print: paper size (A3 fits a 31-day grid with
 * room to spare), shading of blank shifts, one customer per page (a sheet per
 * site for the supervisor), and a guard code -> name list at the end.
 */
const DutyListPrintModal = ({ show, onClose, previewProps }) => {
  const isDetail = previewProps.view === "detail";
  const [paper, setPaper] = useState("A4L");
  const [options, setOptions] = useState({ shadeBlank: true, customerPerPage: false, guardKey: true });
  const toggle = (key) => setOptions((o) => ({ ...o, [key]: !o[key] }));
  const effectivePaper = !isDetail || paper !== "A4P" ? paper : "A4L";

  const handlePrint = () => {
    const bodyHtml = ReactDOMServer.renderToStaticMarkup(<DutyListPrintPreview {...previewProps} options={options} />);
    const win = window.open("", "_blank", "width=1100,height=900");
    if (!win) return;
    win.document.write(
      `<html><head><title>${VIEW_TITLES[previewProps.view]} ${previewProps.subtitle}</title>` +
        `<style>@page { size: ${PAPER[effectivePaper].css}; margin: 7mm; } html,body { margin:0; padding:0; }` +
        ` thead { display: table-header-group; } tr { page-break-inside: avoid; }` +
        ` * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }</style>` +
        `</head><body onload="window.print();window.close();">${bodyHtml}</body></html>`
    );
    win.document.close();
  };

  const rowCount = previewProps.rows?.length || 0;

  return (
    <Modal show={show} onHide={onClose} size="xl" centered scrollable fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {VIEW_TITLES[previewProps.view]}
          <span className="text-muted ms-2 fs-6">print preview</span>
        </Modal.Title>
      </Modal.Header>
      <div className="d-flex flex-wrap align-items-center gap-3 px-3 py-2 border-bottom small">
        <Form.Select size="sm" style={{ width: 170 }} value={effectivePaper} onChange={(e) => setPaper(e.target.value)}>
          {Object.entries(PAPER)
            .filter(([k]) => !isDetail || k !== "A4P")
            .map(([k, p]) => (
              <option key={k} value={k}>
                {p.label}
              </option>
            ))}
        </Form.Select>
        {isDetail && (
          <>
            <Form.Check type="switch" id="dl-shade" label="Shade blank shifts" checked={options.shadeBlank} onChange={() => toggle("shadeBlank")} />
            <Form.Check type="switch" id="dl-percust" label="Each customer on its own page" checked={options.customerPerPage} onChange={() => toggle("customerPerPage")} />
            <Form.Check type="switch" id="dl-key" label="Guard names list at the end" checked={options.guardKey} onChange={() => toggle("guardKey")} />
          </>
        )}
        {isDetail && rowCount > PREVIEW_ROWS && (
          <span className="text-muted ms-auto">Preview shows the first {PREVIEW_ROWS} lines - the print has all {rowCount}.</span>
        )}
      </div>
      <Modal.Body style={{ background: "#e9ecef" }}>
        <div style={{ background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,.2)", width: PAPER[effectivePaper].previewWidth, maxWidth: "100%", margin: "0 auto", overflowX: "auto" }}>
          <DutyListPrintPreview {...previewProps} options={options} limitRows={isDetail ? PREVIEW_ROWS : undefined} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handlePrint} className="d-inline-flex align-items-center gap-2">
          <Printer size={16} /> Print
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DutyListPrintModal;
